import { cn } from '@/lib/utils';

export interface PlatformFooterProps {
  /** `ink` for light surfaces (default), `light` for dark surfaces. */
  tone?: 'ink' | 'light';
  /**
   * Compact density for app/dashboard chrome (admin, clinical, org shells):
   * tighter padding so the footer reads as a quiet baseline rather than a
   * marketing band. The client portal keeps the roomier default.
   */
  compact?: boolean;
  /** Layout hook — horizontal gutter / max-width / mobile clearance. */
  className?: string;
}

/**
 * Legal copyright footer, rendered at the foot of every app shell, the auth
 * form card, and the error/utility pages.
 *
 * This used to pair the copyright with a `POWERED BY ✦ ELIXAA · AI` lockup,
 * laid out `sm:flex-row justify-between` to push the two children to
 * opposite ends of the row. The credit is gone from the product entirely —
 * elixaa.ai is uncertified, so nothing may attribute to it in the browser —
 * which leaves a single centred line: the `sm:flex-row`/`justify-between`
 * pair that distributed two children is gone with it, and `items-center` on
 * the `flex-col` container (where it governs the cross/horizontal axis) is
 * what centres the one child that remains.
 */
export function PlatformFooter({ tone = 'ink', compact = false, className }: PlatformFooterProps) {
  const year = new Date().getFullYear();
  // Copyright is legal/compliance text — kept low-key but at a contrast that
  // clears WCAG AA on the light surfaces it sits on (accent-ink/75 ≈ 5:1).
  const copyrightColor = tone === 'light' ? 'text-white/70' : 'text-accent-ink/75';
  return (
    <footer
      className={cn(
        'flex flex-col items-center border-t',
        compact ? 'py-2.5' : 'py-2.5 sm:py-4',
        tone === 'light' ? 'border-white/15' : 'border-(--color-border)',
        className,
      )}
    >
      {/* The two &nbsp; entities are load-bearing, not stray whitespace: they
          weld "All rights reserved." into one 100.95px atomic token, so the
          greedy line-breaker is forced to break at the period instead of
          mid-sentence. Dropping "& Elixaa.ai" shortened the line but did not
          change that atom, so the constraint is unchanged: the narrowest well
          any consumer gives this paragraph is 188px (auth at 1024), against a
          100.95px widest atom. Written as HTML entities rather than literal
          U+00A0 characters so they stay visible in a diff instead of reading
          as an ordinary space: a normalising editor or a copy-paste can't
          silently collapse or delete what it can plainly see. The tests that
          assert this string collapse whitespace, which is why they stay green
          AND why they cannot catch the fix's removal. */}
      <p className={cn('text-center font-sans text-xs leading-tight', copyrightColor)}>
        Copyright © {year} Myndwell.io. All&nbsp;rights&nbsp;reserved.
      </p>
    </footer>
  );
}
